import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/community/jamie-jones-paradise-unvrs-ibiza-22-july-2026";
const IMAGE_PATH = "/assets/community/cd1c7392-cbe6-4ff4-8137-755f05638272.png";
const IMAGE_URL = `https://www.hey-lets-travel.com${IMAGE_PATH}`;
const TICKET_URL = "https://www.unvrs.com/events/2026/jamie-jones-paradise/22-07-2026";
const CONCIERGE_URL = "/concierge";

const MAIN_ROOM_LINEUP = ["Jamie Jones B2B Franky Rizardo", "Sosa", "ALISHA", "Julian Fijma"];
const WILD_COMET_LINEUP = ["Job de Jong", "Dimmish", "Pirate Copy", "Vanee"];
const BUNKER_LINEUP = ["Abel", "Antares B2B Alpaka", "Cameron Jack", "Ramyen"];
const ALL_ARTISTS = [...MAIN_ROOM_LINEUP, ...WILD_COMET_LINEUP, ...BUNKER_LINEUP];

const FAQS = [
  {
    question: "When is Jamie Jones Presents Paradise at [UNVRS] Ibiza?",
    answer: "Paradise is listed at [UNVRS] Ibiza on Wednesday 22 July 2026, with doors from 23:30.",
  },
  {
    question: "Who is playing Paradise at [UNVRS] on 22 July 2026?",
    answer: "The Main Room lineup is Jamie Jones B2B Franky Rizardo, Sosa, ALISHA and Julian Fijma. Wild Comet features Job de Jong, Dimmish, Pirate Copy and Vanee, with The Bunker hosting Hot Creations & Friends.",
  },
  {
    question: "Where can I buy Paradise [UNVRS] Ibiza tickets?",
    answer: "Use the official [UNVRS] event link in this HLT guide for live availability. The supplied artwork shows from 150 EUR, but ticket release pricing can move.",
  },
  {
    question: "Where is [UNVRS] Ibiza?",
    answer: "[UNVRS] is in San Rafael, Ibiza, on the former Privilege Ibiza site.",
  },
  {
    question: "What should I know before going to [UNVRS]?",
    answer: "[UNVRS] events are 18+. The venue guidance says no flip-flops, beach shorts, swimwear or sports merch, so dress for a proper club night and bring photo ID.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Jamie Jones Presents Paradise at [UNVRS] Ibiza 22 July 2026: Tickets, Lineup and HLT Guide",
  description:
    "Jamie Jones B2B Franky Rizardo lead Paradise at [UNVRS] Ibiza on Wednesday 22 July 2026 with Sosa, ALISHA, Julian Fijma, Job de Jong, Dimmish, Pirate Copy and Vanee.",
  image: [IMAGE_URL],
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-07-22",
  dateModified: "2026-07-22",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const eventSchema = {
  "@context": "https://schema.org",
  "@type": "MusicEvent",
  name: "Jamie Jones Presents Paradise at [UNVRS] Ibiza",
  startDate: "2026-07-22T23:30:00+02:00",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  eventStatus: "https://schema.org/EventScheduled",
  image: [IMAGE_URL],
  location: {
    "@type": "Place",
    name: "[UNVRS] Ibiza",
    address: "San Rafael, Ibiza, Spain",
  },
  performer: ALL_ARTISTS.map((artist) => ({ "@type": "MusicGroup", name: artist })),
  offers: {
    "@type": "Offer",
    url: TICKET_URL,
    priceCurrency: "EUR",
    price: "150",
    availability: "https://schema.org/InStock",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function TicketCta({ children = "Buy Paradise Tickets" }) {
  return (
    <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
      {children} -&gt;
    </a>
  );
}

function ConciergeCta({ children = "Build the Trip With HLT" }) {
  return (
    <Link to={CONCIERGE_URL} className="btn-secondary">
      {children} -&gt;
    </Link>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function JamieJonesParadiseUnvrsJulyArticle() {
  return (
    <>
      <Helmet>
        <title>Paradise [UNVRS] Ibiza 22 July 2026 Tickets | HLT Guide</title>
        <meta
          name="description"
          content="Jamie Jones Presents Paradise at [UNVRS] Ibiza on Wednesday 22 July 2026 with Jamie Jones B2B Franky Rizardo, Sosa, ALISHA, Julian Fijma and more. Official ticket link and HLT guide."
        />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Paradise [UNVRS] Ibiza 22 July 2026 Tickets" />
        <meta property="og:description" content="Official ticket link, lineup and HLT planning guide for Jamie Jones Presents Paradise at [UNVRS] Ibiza." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Paradise [UNVRS] Ibiza 22 July 2026 Tickets" />
        <meta name="twitter:description" content="Jamie Jones B2B Franky Rizardo, Sosa, ALISHA, Julian Fijma and more at [UNVRS] Ibiza." />
        <meta name="twitter:image" content={IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="grid gap-6 lg:grid-cols-[minmax(0,1fr),30rem] lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">HLT Essential · Ibiza Tonight</p>
            <h1 className="mt-4 font-display text-5xl leading-none text-white md:text-7xl">
              Jamie Jones Presents Paradise at [UNVRS] Ibiza: 22 July 2026
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              Paradise lands at [UNVRS] tonight with Jamie Jones going B2B with Franky Rizardo, backed by Sosa, ALISHA and Julian Fijma in the Main Room, plus a deep Wild Comet lineup for the late-night explorers.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TicketCta children="Buy official [UNVRS] tickets" />
              <ConciergeCta children="Plan [UNVRS] with HLT" />
            </div>
          </div>
          <aside className="border border-brand-500/25 bg-[#111111] p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Event details</p>
            <dl className="mt-4 grid gap-3 text-sm text-slate-300">
              <div><dt className="text-white">Event</dt><dd>Jamie Jones Presents Paradise</dd></div>
              <div><dt className="text-white">Venue</dt><dd>[UNVRS] Ibiza</dd></div>
              <div><dt className="text-white">Date</dt><dd>Wednesday 22 July 2026</dd></div>
              <div><dt className="text-white">Time</dt><dd>23:30</dd></div>
              <div><dt className="text-white">Tickets</dt><dd>Official [UNVRS] link; artwork shows from 150 EUR</dd></div>
              <div><dt className="text-white">Sound</dt><dd>Paradise house, tech house, deep groove and Starship Eden production</dd></div>
            </dl>
          </aside>
        </header>

        <figure className="overflow-hidden border border-brand-500/20 bg-black">
          <img src={IMAGE_PATH} alt="Paradise at [UNVRS] Ibiza 22 July 2026 artwork" className="w-full object-cover" />
        </figure>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr),20rem]">
          <div className="space-y-8">
            <ArticleSection title="Why tonight matters">
              <p>
                Paradise is one of Ibiza's most trusted weekly identities, and its 2026 move into [UNVRS] gives the party a much bigger canvas. The venue describes this season as Starship Eden: a cosmic garden world built for high-production Wednesday nights from June into October.
              </p>
              <p>
                Tonight's hook is simple: Jamie Jones B2B Franky Rizardo. Jamie brings the Paradise and Hot Creations DNA, while Franky Rizardo adds crisp Dutch house movement and a rolling, peak-time touch. For a same-night Ibiza decision, that is a strong centrepiece.
              </p>
            </ArticleSection>

            <ArticleSection title="Main Room: Jamie Jones B2B Franky Rizardo">
              <p>
                The Main Room is built around Jamie Jones B2B Franky Rizardo, with Sosa, ALISHA and Julian Fijma shaping the wider arc of the night. Expect a Paradise-first sound: elastic basslines, warm drums, vocal flashes and enough tech-house pressure to keep [UNVRS] moving after midnight.
              </p>
              <p>
                Sosa gives the bill a direct modern club punch, ALISHA brings sharp groove-led momentum, and Julian Fijma adds another rising European thread. It is a lineup made for people who want the energy high without losing the house-music detail.
              </p>
            </ArticleSection>

            <ArticleSection title="Wild Comet and The Bunker">
              <p>
                Wild Comet is not filler here. Job de Jong, Dimmish, Pirate Copy and Vanee give the second room its own identity, leaning into the deeper and more driving side of the Paradise universe. It is worth treating this as part of the journey rather than a quick walk-through.
              </p>
              <p>
                The official listing also adds The Bunker: Hot Creations &amp; Friends, with Abel, Antares B2B Alpaka, Cameron Jack and Ramyen. That gives the night another layer for people who want to roam the building and follow different shades of the label's sound.
              </p>
            </ArticleSection>

            <ArticleSection title="Ticket link and timing">
              <p>
                We are linking directly to the official [UNVRS] Paradise event page for Wednesday 22 July 2026. The event is listed from 23:30, and live ticket releases can change as availability moves. The supplied event artwork shows from 150 EUR, so always check the official page before travelling.
              </p>
              <p>
                If you are already on the island, sort transport before you leave the hotel. [UNVRS] sits in San Rafael, so Playa d'en Bossa, Ibiza Town and San Antonio groups should plan the return journey as carefully as the arrival.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="Buy tickets on [UNVRS]" />
                <ConciergeCta children="Ask HLT to arrange the night" />
              </div>
            </ArticleSection>

            <ArticleSection title="How to plan the night">
              <p>
                Paradise is a late one, so do not burn the whole group out too early. Dinner first, one proper meet-up point, then arrive with enough time to see the venue before the headline section starts to bite. [UNVRS] is built for scale, and the night will feel better if you are not rushing through the door at the last second.
              </p>
              <p>
                The official [UNVRS] guidance lists the night as 18+ and advises against flip-flops, beach shorts, swimwear and sports merch. Keep it clean, bring ID, and make sure every ticket is ready before you hit the queue.
              </p>
            </ArticleSection>

            <ArticleSection title="Where to stay for [UNVRS]">
              <p>
                Ibiza Town is the smoothest all-round base if you want food, drinks and a cleaner transfer route. Playa d'en Bossa works well if your trip also includes Hï Ibiza or Ushuaïa, while San Antonio makes sense for sunset plans and larger groups watching the budget.
              </p>
              <p>
                HLT can help wrap the night properly: hotels, villas, transfers, tables, after-party planning and the little logistics that make a big Ibiza night feel effortless instead of chaotic.
              </p>
            </ArticleSection>

            <ArticleSection title="Event details at a glance">
              <ul className="grid gap-2 text-slate-300">
                <li><span className="text-white">Event:</span> Jamie Jones Presents Paradise</li>
                <li><span className="text-white">Date:</span> Wednesday 22 July 2026</li>
                <li><span className="text-white">Start time:</span> 23:30</li>
                <li><span className="text-white">Main Room:</span> Jamie Jones B2B Franky Rizardo, Sosa, ALISHA, Julian Fijma</li>
                <li><span className="text-white">Wild Comet:</span> Job de Jong, Dimmish, Pirate Copy, Vanee</li>
                <li><span className="text-white">The Bunker:</span> Abel, Antares B2B Alpaka, Cameron Jack, Ramyen</li>
                <li><span className="text-white">Tickets:</span> Official [UNVRS] link; artwork shows from 150 EUR</li>
              </ul>
            </ArticleSection>

            <ArticleSection title="Final word">
              <p>
                If tonight is your big Wednesday in Ibiza, Paradise at [UNVRS] is the obvious premium pick. Jamie Jones B2B Franky Rizardo gives it the headline pull, while the room spread makes it feel like a full venue takeover rather than a single-set ticket.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="Get official Paradise tickets" />
                <ConciergeCta children="Build the Ibiza trip" />
              </div>
            </ArticleSection>

            <ArticleSection title="FAQ">
              <div className="space-y-5">
                {FAQS.map((item) => (
                  <div key={item.question}>
                    <h3 className="font-semibold text-white">{item.question}</h3>
                    <p>{item.answer}</p>
                  </div>
                ))}
              </div>
            </ArticleSection>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <div className="border border-brand-500/25 bg-[#111111] p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Book direct</p>
              <h2 className="mt-3 font-display text-3xl leading-none text-white">Official ticket link</h2>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                Use the official [UNVRS] listing for live availability, current release pricing and table options.
              </p>
              <div className="mt-4">
                <TicketCta children="Open tickets" />
              </div>
            </div>

            <div className="border border-white/10 bg-[#111111] p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">HLT concierge</p>
              <h2 className="mt-3 font-display text-3xl leading-none text-white">Need the full night handled?</h2>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                Tell us your group size, hotel area and budget. We can help with transfers, tables, villas and a cleaner plan for the night.
              </p>
              <div className="mt-4">
                <ConciergeCta children="Start concierge request" />
              </div>
            </div>

            <div className="border border-white/10 bg-[#0d0d0d] p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Good to know</p>
              <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-300">
                <li>Bring photo ID and keep the official ticket ready on your phone.</li>
                <li>Plan San Rafael transport before you leave for the club.</li>
                <li>Dress for a club night: no flip-flops, beachwear or sports merch.</li>
              </ul>
            </div>
          </aside>
        </div>
      </article>
    </>
  );
}
